import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Copy, Send, RefreshCw } from "lucide-react";

// ---------------------------------------------------------------------------
// Small helpers, also used by the Users list in Admin.js
// ---------------------------------------------------------------------------
export function ago(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!(ms >= 0)) return "just now";
  const mins = Math.floor(ms / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days} day${days === 1 ? "" : "s"} ago`;
  return `${Math.floor(days / 30)} months ago`;
}

export function gapText(days) {
  if (days == null) return "";
  if (days < 1) return "more than once a day";
  if (days === 1) return "every day";
  return `every ${days} days`;
}

// "Last visit 3 days ago, usually every 4 days"
export function visitLine(u) {
  if (!u || !u.last_seen) return "No visit recorded yet";
  const gap = gapText(u.typical_gap_days);
  return `Last visit ${ago(u.last_seen)}${gap ? `, usually ${gap}` : ""}`;
}

function Switch({ on, onChange, label, testid }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} data-testid={testid} onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${on ? "bg-cyan-400 border-cyan-300" : "bg-white/10 border-white/20"}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Admin: Notifications (alerts to the owner) and the visit numbers
// ---------------------------------------------------------------------------
export default function AdminNotifications() {
  const [page, setPage] = useState(null);       // what the server last saved, plus the wording for each row
  const [draft, setDraft] = useState(null);     // what is on screen right now
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState("");         // "save", "push", "email" or "topic" while that is running
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/notifications")
      .then((r) => { if (alive) { setPage(r.data); setDraft(r.data.settings); } })
      .catch(() => { if (alive) setFailed(true); });
    api.get("/admin/visit-stats")
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (failed) {
    return (
      <div data-testid="admin-notifications">
        <h2 className="font-display text-2xl font-bold mb-4">Notifications</h2>
        <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6 text-sm text-zinc-400">The notification settings could not be loaded. Refresh the page and try again.</div>
      </div>
    );
  }
  if (!page || !draft) return null;

  const dirty = JSON.stringify({ e: draft.events, i: draft.include, m: draft.email_to || "" }) !==
    JSON.stringify({ e: page.settings.events, i: page.settings.include, m: page.settings.email_to || "" });

  const setEvent = (key, channel, on) => setDraft((d) => ({ ...d, events: { ...d.events, [key]: { ...d.events[key], [channel]: on } } }));
  const setInclude = (channel, field, on) => setDraft((d) => ({ ...d, include: { ...d.include, [channel]: { ...d.include[channel], [field]: on } } }));

  const save = async (newTopic = false) => {
    setBusy(newTopic ? "topic" : "save");
    try {
      const { data } = await api.put("/admin/notifications", {
        events: draft.events, include: draft.include, email_to: draft.email_to || "", new_topic: newTopic,
      });
      setPage(data); setDraft(data.settings);
      toast.success(newTopic ? "New secret name made. Subscribe your phone to it again." : "Notification settings saved");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
      return false;
    } finally { setBusy(""); }
  };

  const makeNewTopic = async () => {
    if (!confirm("Make a new secret name? Your phone will stop getting alerts until you subscribe it to the new name.")) return;
    await save(true);
  };

  const sendTest = async (channel) => {
    if (dirty && !(await save())) return;
    setBusy(channel);
    try {
      const { data } = await api.post("/admin/notifications/test", { channel });
      if (data.ok) toast.success(data.detail); else toast.error(data.detail);
    } catch (e) { toast.error(e.response?.data?.detail || "The test did not go out"); }
    finally { setBusy(""); }
  };

  const sendSummary = async () => {
    if (dirty && !(await save())) return;
    setBusy("digest");
    try {
      const { data } = await api.post("/admin/digest-now");
      if (data.ok) toast.success(data.detail); else toast.error(data.detail);
    } catch (e) { toast.error(e.response?.data?.detail || "The summary did not go out"); }
    finally { setBusy(""); }
  };

  const copyTopic = async () => {
    try { await navigator.clipboard.writeText(draft.topic); toast.success("Copied"); }
    catch (e) { toast.error("Could not copy. Press and hold the name to copy it."); }
  };

  const groups = [["premium", "Premium members"], ["standard", "Standard (free) members"]];

  return (
    <div data-testid="admin-notifications">
      <h2 className="font-display text-2xl font-bold mb-4">Notifications</h2>
      <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6 space-y-8">

        <section>
          <h3 className="font-display text-lg font-bold mb-1">When should I tell you?</h3>
          <p className="text-xs text-zinc-500 mb-3">Switch each alert on or off. Email goes to your inbox. A phone alert buzzes your phone through the free ntfy app.</p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-5 gap-y-3 items-center" data-testid="admin-notify-events">
            <div />
            <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 text-center">Email</div>
            <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 text-center">Phone</div>
            {page.events.map((ev) => (
              <div key={ev.key} className="contents">
                <div className="text-sm">{ev.label}</div>
                <div className="flex justify-center"><Switch on={!!draft.events[ev.key]?.email} onChange={(v) => setEvent(ev.key, "email", v)} label={`Email me: ${ev.label}`} testid={`admin-notify-${ev.key}-email`} /></div>
                <div className="flex justify-center"><Switch on={!!draft.events[ev.key]?.push} onChange={(v) => setEvent(ev.key, "push", v)} label={`Phone alert: ${ev.label}`} testid={`admin-notify-${ev.key}-push`} /></div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="font-display text-lg font-bold mb-1">What should each message say?</h3>
          <p className="text-xs text-zinc-500 mb-3">A message on your phone's lock screen can be read by anyone who looks, so names and email addresses start out off for the phone. With every box off, the message only says what happened.</p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-5 gap-y-3 items-center" data-testid="admin-notify-fields">
            <div />
            <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 text-center">Email</div>
            <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500 text-center">Phone</div>
            {page.fields.map((f) => (
              <div key={f.key} className="contents">
                <div className="text-sm">{f.label}</div>
                <div className="flex justify-center"><Switch on={!!draft.include.email[f.key]} onChange={(v) => setInclude("email", f.key, v)} label={`Email shows: ${f.label}`} testid={`admin-notify-inc-${f.key}-email`} /></div>
                <div className="flex justify-center"><Switch on={!!draft.include.push[f.key]} onChange={(v) => setInclude("push", f.key, v)} label={`Phone shows: ${f.label}`} testid={`admin-notify-inc-${f.key}-push`} /></div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="font-display text-lg font-bold mb-1">Where do alerts go?</h3>
          <label htmlFor="admin-notify-email-to" className="block text-sm mt-3 mb-1">Email address for alerts</label>
          <input id="admin-notify-email-to" type="email" value={draft.email_to || ""} onChange={(e) => setDraft((d) => ({ ...d, email_to: e.target.value }))}
            placeholder={page.default_email ? `Leave empty to use ${page.default_email}` : "you@example.com"} data-testid="admin-notify-email-to"
            className="w-full sm:max-w-md bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />

          <div className="mt-5">
            <div className="text-sm font-semibold">Your phone</div>
            <ol className="text-sm text-zinc-300 list-decimal pl-5 space-y-1 mt-2">
              <li>On your phone, install the free app called <strong>ntfy</strong> (App Store or Google Play).</li>
              <li>Open it, tap the plus (+) button, and choose to subscribe to a topic.</li>
              <li>Type this secret name exactly, then subscribe. Keep it private, since anyone who knows it can read your alerts.</li>
            </ol>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <code data-testid="admin-notify-topic" className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-cyan-300 text-sm break-all select-all">{draft.topic}</code>
              <button type="button" onClick={copyTopic} data-testid="admin-notify-copy" className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
              <button type="button" disabled={!!busy} onClick={makeNewTopic} data-testid="admin-notify-newtopic" className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm flex items-center gap-1.5 disabled:opacity-50">
                <RefreshCw className="w-3.5 h-3.5" /> Make a new secret name
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button type="button" disabled={!!busy || !dirty} onClick={() => save()} data-testid="admin-notify-save"
              className="px-4 py-2 rounded-lg bg-cyan-400 text-black text-sm font-semibold disabled:opacity-50">{busy === "save" ? "Saving..." : "Save"}</button>
            <button type="button" disabled={!!busy} onClick={() => sendTest("push")} data-testid="admin-notify-test-push"
              className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm flex items-center gap-1.5 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {busy === "push" ? "Sending..." : "Send a test to my phone"}
            </button>
            <button type="button" disabled={!!busy} onClick={() => sendTest("email")} data-testid="admin-notify-test-email"
              className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm flex items-center gap-1.5 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {busy === "email" ? "Sending..." : "Send a test email"}
            </button>
            <button type="button" disabled={!!busy} onClick={sendSummary} data-testid="admin-notify-test-digest"
              className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 text-sm flex items-center gap-1.5 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {busy === "digest" ? "Sending..." : "Send this week's summary now"}
            </button>
            {dirty && <span className="text-xs text-amber-300" data-testid="admin-notify-unsaved">You have changes that are not saved yet.</span>}
          </div>
        </section>

        <section>
          <h3 className="font-display text-lg font-bold mb-1">How often members come back</h3>
          <p className="text-xs text-zinc-500 mb-3">Counted from the day this was switched on. A visit is the first time someone opens the site after being away for half an hour. Admins are left out.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-visit-stats">
            {groups.map(([key, label]) => {
              const g = stats && stats[key];
              return (
                <div key={key} className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500">{label}</div>
                  {!g ? (
                    <div className="text-sm text-zinc-500 mt-2">Loading...</div>
                  ) : (
                    <dl className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-zinc-400">Members</dt><dd className="font-semibold">{g.members.toLocaleString()}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-zinc-400">Came in the last 7 days</dt><dd className="font-semibold">{g.seen_7d.toLocaleString()}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-zinc-400">Usual time between visits</dt><dd className="font-semibold">{g.typical_gap_days == null ? "Not enough yet" : gapText(g.typical_gap_days)}</dd></div>
                    </dl>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
